import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../config";

const VerificationCode = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    code: "",
  });
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Initialize email from local storage if available
  useEffect(() => {
    const storedEmail = localStorage.getItem("user_email");
    if (storedEmail) {
      setFormData((prev) => ({
        ...prev,
        email: storedEmail,
      }));
    }
  }, []);

  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For code field, only allow numbers and max 6 digits
    if (name === "code" && value.length <= 6) {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else if (name !== "code") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleResendCode = async () => {
    if (!formData.email) {
      toast.error("Please enter your email address first");
      return;
    }

    if (resendDisabled) {
      return;
    }

    setResendDisabled(true);
    setCountdown(60); // 60 seconds cooldown

    try {
      // Create FormData object for the request
      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);

      const response = await axios.post(
        `${API_URL}/auth/request-verification`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (
        response.data &&
        response.data.message === "New verification code was sent!"
      ) {
        toast.success("New verification code sent to your email");
      } else {
        toast.success("Verification code has been resent to your email");
      }
    } catch (err) {
      toast.error("Failed to resend verification code. Please try again.");
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.code) {
      toast.error("Please enter both email and verification code");
      return;
    }

    if (formData.code.length !== 6) {
      toast.error("Verification code must be 6 digits");
      return;
    }

    setLoading(true);

    try {
      // Create FormData object for the request
      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);
      formDataToSend.append("code", formData.code);

      const response = await axios.post(
        `${API_URL}/auth/verify-email`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (
        response.data &&
        response.data.message === "Email verified successfully"
      ) {
        toast.success("Email verification successful!");

        // Small delay to show success message before redirect
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Verification failed. Please check your code and try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1F9BFF1A] flex">
      {/* Left side - Verification form */}
      <div className="w-full md:w-1/2 flex items-center justify-center">
        <div className="max-w-lg w-full space-y-4 bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200 m-2 md:m-6">
          <div className="flex flex-col items-center justify-center p-4 md:p-8">
            <div className="self-start">
              <h2 className="text-3xl font-bold text-gray-800 mb-1">
                Verify Your Email
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Enter the 6-digit verification code sent to your email.
              </p>
            </div>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <form onSubmit={handleSubmit} className="mt-6 space-y-4 w-full">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-500"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-gray-500"
                >
                  Verification Code
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter 6-digit code"
                />
                <div className="flex justify-between">
                  <div className="flex-grow"></div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.code.length}/6
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1F9BFF] hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    "Verify Email"
                  )}
                </button>
              </div>

              <div className="flex justify-center">
                <p className="text-sm text-gray-600">
                  Didn't receive a code?{" "}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className={`${
                      resendDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-blue-400 hover:text-blue-500"
                    } focus:outline-none`}
                    disabled={resendDisabled}
                  >
                    {resendDisabled ? `Resend in ${countdown}s` : "Resend code"}
                  </button>
                </p>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="text-sm text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - Blue section with text and logo */}
      <div className="hidden md:flex w-3/5 bg-[#1F9BFF1A] flex-col items-center justify-center p-12 text-white">
        <div className="space-y-6">
          <img src="/Efficia-01.svg" alt="Logo" className="h-15" />
          <h1 className="text-6xl font-bold text-blue-400 mb-4">
            Verify Your Account
          </h1>
          <p className="text-lg text-gray-600">
            We've sent a verification code to your email address. Please check
            your inbox and enter the code to complete your registration.
          </p>
          <p className="text-lg text-gray-600">
            Once verified, you'll have full access to manage your emails with
            our AI-powered assistant.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationCode;
